import { Field, ID, ObjectType, registerEnumType } from 'type-graphql'
import {
  Column,
  CreateDateColumn,
  Entity,
  Index,
  JoinColumn,
  ManyToOne,
  OneToOne,
  PrimaryGeneratedColumn,
  RelationId,
  UpdateDateColumn
} from 'typeorm'

import { User } from '@things-factory/auth-base'
import { config } from '@things-factory/env'
import { Domain, ScalarObject } from '@things-factory/shell'
import { ActivityInstance } from '@things-factory/worklist'

import { DataItem } from '../data-set/data-item-type.js'
import { DataSet } from '../data-set/data-set.js'
import { DataSample } from '../data-sample/data-sample.js'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type

export enum DataOocStatus {
  ISSUED = 'ISSUED',
  REVIEWED = 'REVIEWED',
  CORRECTED = 'CORRECTED'
}

registerEnumType(DataOocStatus, {
  name: 'DataOocStatus',
  description: 'Out of control data corrective action process progress status'
})

@Entity()
@Index('ix_data_ooc_0', (dataOoc: DataOoc) => [dataOoc.domain, dataOoc.dataSet, dataOoc.collectedAt], { unique: false })
@Index('ix_data_ooc_1', (dataOoc: DataOoc) => [dataOoc.domain, dataOoc.workDate, dataOoc.name])
@ObjectType({
  description:
    'Represents an out-of-control (OOC) quality event that requires corrective action. This entity tracks the resolution workflow and maintains history of quality issues.'
})
export class DataOoc {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for the OOC record.' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field(type => Domain)
  domain?: Domain

  @RelationId((dataOoc: DataOoc) => dataOoc.domain)
  domainId?: string

  @Column()
  @Field({ nullable: true })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  description?: string

  @ManyToOne(type => DataSet)
  @Field(type => DataSet, { nullable: true })
  dataSet?: DataSet

  @RelationId((dataOoc: DataOoc) => dataOoc.dataSet)
  dataSetId?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  dataSetVersion?: number

  @Column({ nullable: true })
  @Field({ nullable: true })
  ooc?: boolean

  @Column({ nullable: true })
  @Field({ nullable: true })
  oos?: boolean

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Current status of the OOC event in the corrective action workflow.' })
  state?: DataOocStatus

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'Instructions for corrective action to resolve the OOC issue.' })
  correctiveInstruction?: string

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true, description: 'Description of the corrective action taken to resolve the OOC issue.' })
  correctiveAction?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  type?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  useCase?: string

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key01?: string = ''

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key02?: string = ''

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key03?: string = ''

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key04?: string = ''

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key05?: string = ''

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, { nullable: true })
  partitionKeys?: { [key: string]: any }

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, { nullable: true })
  data?: any

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, { nullable: true })
  judgment?: any

  @Field(type => [DataItem], { nullable: true })
  dataItems?: DataItem[]

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Complete history of the OOC event, including all status changes and actions taken.'
  })
  history?: any

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({ nullable: true })
  rawData?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  source?: string

  @OneToOne(type => DataSample, { nullable: true })
  @JoinColumn()
  @Field(type => DataSample, { nullable: true })
  dataSample?: DataSample

  @RelationId((dataOoc: DataOoc) => dataOoc.dataSample)
  dataSampleId?: string

  @OneToOne(type => ActivityInstance, { nullable: true })
  @JoinColumn()
  @Field({ nullable: true })
  reviewActivityInstance?: ActivityInstance

  @RelationId((dataOoc: DataOoc) => dataOoc.reviewActivityInstance)
  reviewActivityInstanceId?: string

  @OneToOne(type => ActivityInstance, { nullable: true })
  @JoinColumn()
  @Field({ nullable: true })
  resolveActivityInstance?: ActivityInstance

  @RelationId((dataOoc: DataOoc) => dataOoc.resolveActivityInstance)
  resolveActivityInstanceId?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  workDate?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  workShift?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  collectedAt?: Date

  @Column({ nullable: true })
  @Field({ nullable: true })
  reviewedAt?: Date

  @Column({ nullable: true })
  @Field({ nullable: true })
  correctedAt?: Date

  @CreateDateColumn()
  @Field({ nullable: true })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field({ nullable: true })
  reviewer?: User

  @RelationId((dataOoc: DataOoc) => dataOoc.reviewer)
  reviewerId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field({ nullable: true })
  corrector?: User

  @RelationId((dataOoc: DataOoc) => dataOoc.corrector)
  correctorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  creator?: User

  @RelationId((dataOoc: DataOoc) => dataOoc.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  updater?: User

  @RelationId((dataOoc: DataOoc) => dataOoc.updater)
  updaterId?: string
}
